import React, { useEffect, useState } from 'react';
import TenantSource from '../../sources/tenant';
import EditorActions from '../../actions/editor';
import PageActions from '../../actions/page';
import TabContent from '../tabs/TabContent';
import jwtDecode from 'jwt-decode';
import Loader from '../Loader';

import { addNotification, removeAllNotifications } from '../../actions/reduxActions/notification';
import { fetchCurrentTenant } from '../../actions/reduxActions/currentTenant';
import { setCurrentUser } from '../../actions/reduxActions/currentUser';
import { connect } from 'react-redux';
import { pathOr } from 'ramda';
import { Redirect, useMatch } from '@reach/router';
import { setSharingToken, setImportToken } from '../../actions/reduxActions/flows';

const { pendo } = window;

/**
 *
 * @param {String} authenticationToken
 * @param {Array} editors
 * @param {Function} getTenant
 * @param {Function} onError
 * @param {Function} clearNotifications
 * @param {Array} tenants
 * @param {Function} setSharingToken
 * @param {Function} setImportToken
 * @param {Function} setCurrentUserAction
 * @param {Boolean} isSso
 * @param {String} roleName
 * @description Determines which tenant the user should be authorized with.
 * If the tenant ID in the url changes then this will trigger a tenant switch.
 */
const Tenant = ({
    authenticationToken,
    editors,
    fetchCurrentTenant: getTenant,
    addNotification: onError,
    removeAllNotifications: clearNotifications,
    tenants,
    setSharingToken,
    setImportToken,
    setCurrentUserAction,
    isSso,
    roleName,
}) => {
    const [isAuthorized, authorize] = useState(true);
    const [isLoading, updateLoader] = useState(true);
    const [token, updateToken] = useState(authenticationToken);

    const match = useMatch('/:tenantid/*');
    const noTabmatch = useMatch('/:tenantid');

    // We only get the tenant details when the users list of
    // available tenants have been added to the store
    const tenantDetails = tenants.length > 0
        ? tenants.find(t => t.id === match.tenantid)
        : null;

    const tenantDetailsId = pathOr(null, ['id'], tenantDetails);

    useEffect(() => {
        if (!isLoading) {
            updateLoader(true);
        }

        clearNotifications();
        setSharingToken({ token: null });
        setImportToken(null);

        // Only attempt to switch tenant once tenantDetails are retrieved,
        // as this contains all the necessary info about the tenant that we send off
        // to Pendo eg. org name etc
        if (!isSso && tenantDetailsId) {
            TenantSource.switch(match.tenantid, authenticationToken)
                .then((tenantToken) => {
                    getTenant(tenantToken, match.tenantid);
                    const userClaims = jwtDecode(tenantToken);

                    EditorActions.closeAll(false);
                    PageActions.clear();

                    let orgId = null;
                    let orgName = null;
                    if (pathOr(null, ['organization'], tenantDetails)) {
                        orgId = tenantDetails.organization.id || null;
                        orgName = tenantDetails.organization.name || null;
                    }

                    setCurrentUserAction({
                        orgId,
                        orgName,
                        authenticationToken: tenantToken,
                        tenantId: match.tenantid,
                        tenant: match.tenantId,
                        tenantName: userClaims.tenant_name,
                        roleName: pathOr('', ['role_name'], userClaims),
                    });

                    if (pendo && userClaims) {
                        const options = {
                            visitor: {
                                id: pendo.visitorId ? pendo.visitorId : userClaims.sub,
                                email: userClaims.email,
                                full_name: `${userClaims.given_name} ${userClaims.family_name}`,
                            },
                            account: {
                                id: match.tenantid,
                                name: userClaims.tenant_name,
                            },
                            parentAccount: {
                                id: orgId || 'no-organization',
                                name: orgName,
                            },
                        };

                        if (pendo.visitorId) {
                            pendo.identify(options);
                        } else {
                            pendo.initialize(options);
                        }
                    }

                    updateToken(tenantToken);
                    updateLoader(false);
                })
                .catch(({ message }) => {
                    onError({
                        type: 'error',
                        message,
                        isPersistent: true,
                    });
                    setCurrentUserAction({ tenantId: null, tenant: null, tenantName: null });
                    authorize(false);
                });
        } else {
            updateLoader(false);
        }
    }, [match.tenantid, tenantDetailsId]);

    // User has authenticated but has failed authorizing with a tenant
    if (!isAuthorized && authenticationToken !== null) {
        // Redirecting to the root url will trigger the tenant selector modal to open
        return <Redirect to="/" noThrow />;
    }

    if (noTabmatch) {
        return <Redirect to="flows" noThrow />;
    }

    if (isAuthorized && !isLoading && roleName) {
        return (
            <TabContent
                editors={editors}
                adminTenantId={manywho.settings.global('adminTenantId')}
                authenticationToken={token}
            />
        );
    }

    return <Loader />;
};

const mapStateToProps = ({ currentUser: { tenants, roleName } }) => ({ tenants, roleName });

const mapDispatchToProps = {
    fetchCurrentTenant,
    addNotification,
    removeAllNotifications,
    setSharingToken,
    setImportToken,
    setCurrentUserAction: setCurrentUser,
};

export default connect(mapStateToProps, mapDispatchToProps)(Tenant);
